//copocreation.tsx
import React, { useRef, useState, useEffect } from "react";
import {
    Paper,
    Stepper,
    Step,
    StepLabel,
    Breadcrumbs,
    Typography,
    Box,
    Grid2,
    Button
} from "@mui/material";
import BtnFooter from "./BtnFooter";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import { BasicInformation } from "./BasicInformation";
import { GeneralDetails } from "./GeneralDetails";
import { useDispatch, useSelector } from "react-redux";
import {
    getCopoBasicInfoSuccess,
    setCopoBasicInfoFormData,
    resetCopoData
} from "../../Redux/CoPo/actions";
import {
    getCopoAutoPopulateData,
    submitCopoFormData,
    uploadCoPoFileAPI
} from "../Services/MMRfqForm";
import { showToast } from "../../Components/Toast";
import { useBeforeUnloadPrompt } from "../../hooks/useUnsavedChangesPrompt";
import { usePrompt } from "../../hooks/usePrompt";
import { ThreeDot } from "react-loading-indicators";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Backdrop,
    CircularProgress,
    Button as MuiButton,
    IconButton
} from "@mui/material";

export default function CoPoCreation() {
    const navigate = useNavigate();
    const location = useLocation();
    const rfqData = location.state?.rfqData;

    const corfqId =
        rfqData?.rfqID || sessionStorage.getItem("temp_copo_display_id") || "";
    const CORFQ_ID =
        rfqData?.ID || sessionStorage.getItem("temp_copo_CORFQ_ID") || "";

    const [activeStep, setActiveStep] = useState(0);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isAutoPopulateLoading, setIsAutoPopulateLoading] = useState(false);
    const steps = ["Basic Information", "General Details"];

    const handleBack = () => setActiveStep((prev) => prev - 1);

    const copoData = useSelector((state: any) => state.CoPoReducer);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [isFormTouched, setIsFormTouched] = useState(false);

    useBeforeUnloadPrompt(isFormTouched, "Changes you made may not be saved.");
    usePrompt("Changes you made may not be saved.", isFormTouched);

    const handleConfirmSubmit = () => {
        if (!generalDetailsRef.current?.validateAndShowErrors()) {
            return;
        }
        setOpenConfirmDialog(true);
    };

    const handleCopoSubmission = async () => {
        setOpenConfirmDialog(false);
        setIsSubmitting(true);

        const finalSubmittedData = {
            data: {
                basicInfo: copoData.basicInfoFormData,
                generalDetails: copoData.generalDetailsFormData
            }
        };

        // console.log("Final Submitted Co-Po Data:", finalSubmittedData);

        try {
            const response = await submitCopoFormData(finalSubmittedData);
            console.log("Submit Response:", response);
            if (
                response?.code === 3000 ||
                response?.status === "success" ||
                response?.data
            ) {
                // Adjust success check based on actual API response structure if known,
                // for now assumes standard success or just proceeds

                const recordId =
                    response?.data?.id || response?.data || response?.id;
                const file = copoData.generalDetailsFormData?.copoFile;

                if (recordId && file) {
                    try {
                        const uploadRes = await uploadCoPoFileAPI({
                            recordId: String(recordId),
                            uploadField: "Upload_COPO",
                            fileUpload: file
                        });
                        if (!response?.status) {
                            showToast({
                                type: "error",
                                message: response?.data
                            });
                        }
                        console.log("File upload response:", uploadRes);
                    } catch (uploadError) {
                        console.error("File upload failed:", uploadError);
                    }
                }
                showToast({
                    type: "success",
                    message: "COPO created"
                });

                setIsFormTouched(false);
                setIsSubmitted(true);
                dispatch(resetCopoData());
                setTimeout(() => {
                    navigate("/layout/Proposals");
                }, 0);
            } else {
                // Fallback if structure varies, still alert success as per previous flow request or just log
                // Keeping original user flow of alerting "Form submitted!"
                showToast({
                    type: "error",
                    message: response?.data
                });
            }
        } catch (e) {
            console.error("Error submitting form:", e);
            showToast({
                type: "error",
                message: "Error submitting form"
            });
            setIsSubmitting(false);
        }
    };

    const dispatch = useDispatch();

    const handleCancelExit = () => {
        dispatch(resetCopoData());
        navigate("/layout/Proposals");
    };

    const basicInfoRef = useRef<any>(null);
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        if (rfqData?.ID) {
            sessionStorage.setItem("temp_copo_CORFQ_ID", rfqData.ID);
        }
        if (rfqData?.rfqID) {
            sessionStorage.setItem("temp_copo_display_id", rfqData.rfqID);
        }
        if (rfqData?.companyID) {
            sessionStorage.setItem("temp_copo_companyID", rfqData.companyID);
        }
        if (rfqData?.companyName) {
            sessionStorage.setItem(
                "temp_copo_companyName",
                rfqData.companyName
            );
        }
        if (rfqData?.keyAccountManager) {
            sessionStorage.setItem(
                "temp_copo_keyAccountManager",
                rfqData.keyAccountManager
            );
        }
    }, [rfqData]);

    useEffect(() => {
        dispatch(resetCopoData());
        setIsInitialized(true);

        const fetchBasicInfoCopo = async () => {
            setIsAutoPopulateLoading(true);
            try {
                // console.log("corfqId", CORFQ_ID);

                const response = await getCopoAutoPopulateData(CORFQ_ID);

                if (response?.status) {
                    const outputData = response?.data;
                    const dataToUse = Array.isArray(outputData)
                        ? outputData[0]
                        : outputData;

                    if (dataToUse) {
                        console.log(
                            "Dispatching autoPopulateData from Parent:",
                            dataToUse
                        );
                        dispatch(getCopoBasicInfoSuccess(dataToUse));
                    }
                } else {
                    dispatch(resetCopoData());
                    showToast({
                        type: "error",
                        message: response?.message
                    });
                }
            } catch (error) {
                console.error(error);
                dispatch(resetCopoData());
            } finally {
                setIsAutoPopulateLoading(false);
            }
        };

        fetchBasicInfoCopo();
    }, []);

    const isFormTouchedRef = useRef(isFormTouched);
    useEffect(() => {
        isFormTouchedRef.current = isFormTouched;
    }, [isFormTouched]);

    const onBackButtonEvent = (e: any) => {
        e.preventDefault();
        if (isFormTouchedRef.current) {
            const confirmLeave = window.confirm(
                "Changes you made may not be saved."
            );
            if (confirmLeave) {
                dispatch(resetCopoData());
                navigate("/layout/Proposals"); // Navigate away
            } else {
                window.history.pushState(null, "", window.location.pathname);
            }
        } else {
            // If not touched, just allow back navigation (or force it if state was pushed)
            // Since we are pushing state, "back" effectively happened (popped one state).
            // We should probably just navigate away to be consistent with "Back means exit".
            dispatch(resetCopoData());
            navigate("/layout/Proposals");
        }
    };

    useEffect(() => {
        window.history.pushState(null, "", window.location.pathname);
        window.addEventListener("popstate", onBackButtonEvent);
        return () => {
            window.removeEventListener("popstate", onBackButtonEvent);
        };
    }, []);

    const handleNext = () => {
        if (activeStep === 0) {
            if (!basicInfoRef.current?.validate()) {
                return; // stay on step 0
            }
            const data = basicInfoRef.current?.getData();
            dispatch(setCopoBasicInfoFormData(data));
        }
        setActiveStep((prev) => prev + 1);
    };

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <BasicInformation
                        setIsFormTouched={setIsFormTouched}
                        isAutoPopulateLoading={isAutoPopulateLoading}
                        ref={basicInfoRef}
                    />
                );
            case 1:
                return (
                    <GeneralDetails
                        setIsFormTouched={setIsFormTouched}
                        ref={generalDetailsRef}
                    />
                );
            default:
                return null;
        }
    };

    const generalDetailsRef = useRef<any>(null);

    const isFormValid =
        activeStep === 1 && generalDetailsRef.current?.isFormValid();

    return (
        <>
            <Breadcrumbs aria-label="breadcrumb" sx={{ marginBottom: "10px" }}>
                <Typography sx={{ fontSize: "14px" }}>
                    <Link
                        to="/layout/Dashboard"
                        style={{
                            color: "#0070BA",
                            fontSize: "14px",
                            textDecoration: "none"
                        }}
                    >
                        Home
                    </Link>
                </Typography>
                <Typography sx={{ fontSize: "14px" }}>
                    <Link
                        to="/layout/Proposals"
                        style={{
                            color: "#0070BA",
                            fontSize: "14px",
                            textDecoration: "none"
                        }}
                    >
                        Proposals
                    </Link>
                </Typography>
                <Typography key="final" sx={{ fontSize: "14px" }}>
                    Company PO form
                </Typography>
            </Breadcrumbs>
            <Typography variant="h5" mb={1}>
                Company PO form
            </Typography>
            <Paper
                sx={{ marginBottom: "10px", boxShadow: "0 2px 2px 0 #e7e7e7" }}
            >
                <Grid container p={2} justifyContent={"center"}>
                    <Grid size={{ md: 6, xs: 12 }}>
                        <Stepper activeStep={activeStep}>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Grid>
                </Grid>
            </Paper>

            <Paper sx={{ p: 4 }}>
                {isInitialized && renderStepContent()}

                <BtnFooter
                    activeStep={activeStep}
                    corfqId={corfqId}
                    isFormValid={Boolean(isFormValid)}
                    totalSteps={steps.length}
                    onNext={handleNext}
                    onBack={handleBack}
                    onSubmit={handleConfirmSubmit}
                    onCancelExit={handleCancelExit}
                />
            </Paper>

            {/* Confirmation Dialog */}
            <Dialog
                open={openConfirmDialog}
                onClose={() => setOpenConfirmDialog(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6">
                        Create Customer Purchase Order
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to add the COPO for {corfqId}?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOpenConfirmDialog(false)}
                        color="error"
                        variant="text"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCopoSubmission}
                        variant="text"
                        sx={{ color: "#1976d2" }}
                    >
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>

            <Backdrop
                sx={{
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backgroundColor: "rgba(0,0,0,0.7)"
                }}
                open={isSubmitting}
            >
                <ThreeDot color="#1976d2" size="medium" text="" textColor="" />
            </Backdrop>
        </>
    );
}
